/**
 * Silverpeak brand style configuration — colors, typography, and prompt
 * rules extracted from the client's 20-page template (slide master
 * colorScheme "Custom 1" + fontScheme "Arial", layout placeholder
 * geometry, via the OOXML).
 *
 * The master's first layout is "Front Cover". Unqualified body inserts
 * use "S1 BLUE - 1 column title" (first catalog match, slideLayout5).
 * That layout has a label and a title band only — no large body
 * placeholder. Duplicate layout names exist; the first catalog match wins.
 */

import type { ThemeColors } from "../../types";

export const SILVERPEAK_COLORS = {
  charcoal: "404040", // dk1 / dk2
  offWhite: "F8F8F8", // lt2
  white: "FFFFFF", // lt1
  teal: "006A89", // accent1
  aqua: "64B1BB", // accent2
  orange: "F0770C", // accent3
  lightBlue: "6AABD2", // accent4
  green: "3F8F79", // accent5
  cyan: "27A0C2", // accent6
  link: "5F5F5F", // hlink
  followedLink: "919191", // folHlink

  up: "00B050",
  down: "C00000",
} as const;

export const SILVERPEAK_THEME_COLORS: ThemeColors = {
  dark1: SILVERPEAK_COLORS.charcoal,
  dark2: SILVERPEAK_COLORS.charcoal,
  light1: SILVERPEAK_COLORS.white,
  light2: SILVERPEAK_COLORS.offWhite,
  accent1: SILVERPEAK_COLORS.teal,
  accent2: SILVERPEAK_COLORS.aqua,
  accent3: SILVERPEAK_COLORS.orange,
  accent4: SILVERPEAK_COLORS.lightBlue,
  accent5: SILVERPEAK_COLORS.green,
  accent6: SILVERPEAK_COLORS.cyan,
  hyperlink: SILVERPEAK_COLORS.link,
  followedHyperlink: SILVERPEAK_COLORS.followedLink,
  majorFont: "Arial",
  minorFont: "Arial",
};

export const SILVERPEAK_CHART_SERIES_PALETTE = [
  SILVERPEAK_COLORS.teal,
  SILVERPEAK_COLORS.aqua,
  SILVERPEAK_COLORS.orange,
  SILVERPEAK_COLORS.lightBlue,
  SILVERPEAK_COLORS.green,
  SILVERPEAK_COLORS.cyan,
];

export const SILVERPEAK_SLIDE_DIMENSIONS = { width: 960, height: 540 } as const;

export const SILVERPEAK_POSITIONS = {
  label: { left: 66.0, top: 18.1, width: 414.0, height: 12.1 },
  title: { left: 66.0, top: 44.0, width: 827.9, height: 66.6 },
  contentStart: { left: 66.0, top: 120.0 },
  content: { left: 66.0, top: 120.0, width: 828.0, height: 380.0 },
} as const;

export const SILVERPEAK_STYLE_PROMPT = `

## SILVERPEAK BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

REFERENCE PRECEDENTS: The attached "Silverpeak.pptx" is the Silverpeak visual standard. Match the closest relevant precedent for covers, one-column pages, two-column pages, and section slides. Prefer its teal-led palette, Arial type, and title band over inventing a generic layout.

CRITICAL FONT RULE: You MUST include \`fontFamily\` on EVERY shape you create or modify — even when editing existing text. This OVERRIDES the general rule about matching the existing presentation's fonts. Silverpeak uses:
- Titles / headings / body / bullets / tables / charts / notes / subtitles: \`"Arial"\`
NEVER omit fontFamily when Silverpeak style is active. If a shape already has the wrong font (including Calibri), include fontFamily to correct it to Arial.

### Color palette (from the Silverpeak master colorScheme "Custom 1")
Use ONLY these Silverpeak brand colors. Do NOT use arbitrary hex values.

**Theme mapping:**
- Text1 / dark1: Charcoal #${SILVERPEAK_COLORS.charcoal}
- Text2 / dark2: Charcoal #${SILVERPEAK_COLORS.charcoal}
- Background1 / light1: White #${SILVERPEAK_COLORS.white}
- Background2 / light2: Off-white #${SILVERPEAK_COLORS.offWhite}
- Accent1: Teal #${SILVERPEAK_COLORS.teal}
- Accent2: Aqua #${SILVERPEAK_COLORS.aqua}
- Accent3: Orange #${SILVERPEAK_COLORS.orange}
- Accent4: Light blue #${SILVERPEAK_COLORS.lightBlue}
- Accent5: Green #${SILVERPEAK_COLORS.green}
- Accent6: Cyan #${SILVERPEAK_COLORS.cyan}

**Usage:**
- **Primary brand / emphasis**: Teal #${SILVERPEAK_COLORS.teal}
- **Secondary emphasis**: Aqua #${SILVERPEAK_COLORS.aqua} or cyan #${SILVERPEAK_COLORS.cyan}
- **Highlight**: Orange #${SILVERPEAK_COLORS.orange} — a highlight, not the default body colour
- **Text**: Charcoal #${SILVERPEAK_COLORS.charcoal}
- **Background / shading**: Off-white #${SILVERPEAK_COLORS.offWhite} or white #${SILVERPEAK_COLORS.white}
- **Up / down**: Green #${SILVERPEAK_COLORS.up} / red #${SILVERPEAK_COLORS.down}
- **Chart series palette** (in order): ${SILVERPEAK_CHART_SERIES_PALETTE.map((c) => `#${c}`).join(", ")}
  Lead with teal (#${SILVERPEAK_COLORS.teal}) as the first/primary series color.

### Typography (authoritative — from "S1 BLUE - 1 column title")
Slide dimensions: 960 × 540 pt (16:9).

The default body layout places only two on-page placeholders. It does NOT contain a large body placeholder. Body copy is freeform below the title band.

**Placeholder positions:**
- **Label** ("Text Placeholder 28", body idx 13): left=${SILVERPEAK_POSITIONS.label.left}, top=${SILVERPEAK_POSITIONS.label.top}, width=${SILVERPEAK_POSITIONS.label.width}, height=${SILVERPEAK_POSITIONS.label.height} — Arial, about 10pt, charcoal.
- **Title** ("Text Placeholder 8", body idx 14): left=${SILVERPEAK_POSITIONS.title.left}, top=${SILVERPEAK_POSITIONS.title.top}, width=${SILVERPEAK_POSITIONS.title.width}, height=${SILVERPEAK_POSITIONS.title.height} — Arial, about 24pt.
- **Body**: starts at left=${SILVERPEAK_POSITIONS.contentStart.left}, top=${SILVERPEAK_POSITIONS.contentStart.top}, across about ${SILVERPEAK_POSITIONS.content.width} pt. Size body copy to the nearest reference slide. Do not invent a full-width content placeholder.

**Layout choice:**
- Default body: "S1 BLUE - 1 column title". The name is duplicated; the first catalog match is the one-column title band above.
- Do not default to "Front Cover" (the master's first layout).
- Two-column, section, pillar, and colour-variant layouts (grey, green, "Three pillars", "Section page") are specialist. Clone them only for the same job. Do not list or rotate through the rest of the 100+ layouts.

### Charts (Silverpeak style)
- Font: Arial, minimum 8pt
- **Primary data color**: teal (#${SILVERPEAK_COLORS.teal}); multi-series uses the series palette above in order
- Data labels: positioned outside bars/columns (inside for stacked)
- Gridlines: minimal — none by default; only when no data labels are shown
- Legend: bottom-center placement (only when needed for multi-series)
- No 3D effects, gradients, or graphic fills

### Tables (Silverpeak style)
- Header row: teal (#${SILVERPEAK_COLORS.teal}) fill with white text, bold
- Body: white or off-white fill, charcoal Arial text
- Borders: thin charcoal or teal rules; align numeric columns consistently

### Shapes & layout (Silverpeak style)
- Separate content with sharp rectangular blocks — not rounded shapes
- Page furniture stays on the title band; do not recreate a logo that is not on the layout
- Prefer "S1 BLUE - 1 column title" for ordinary body slides

**TEXT FITTING RULE**: Keep labels and item descriptions SHORT (max 6-8 words each). The title band is wide but the label is only about 414 pt. Use concise labels, not full sentences.
`;
